import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { get, last } from "lodash";
import c from "classnames";
import {
  getTxAllCanonicalMsgs,
  createLogMatcherForActions
} from "@terra-money/log-finder-ruleset";
import { formatDistanceToNowStrict } from "date-fns";
import apiClient from "../../apiClient";
import Finder from "../../components/Finder";
import MsgBox from "../../components/MsgBox";
import Copy from "../../components/Copy";
import Icon from "../../components/Icon";
import { useLogfinderActionRuleSet } from "../../hooks/useLogfinder";
import { useCurrentChain } from "../../contexts/ChainsContext";
import { useGetQueryURL } from "../../queries/query";
import format from "../../scripts/format";
import Pending from "./Pending";
import Searching from "./Searching";
import TxAmount from "./TxAmount";
import TaxRateAmount from "./TaxRateAmount";
import { transformTx } from "./transform";
import s from "./Tx.module.scss";
import { useIsClassic } from "../../contexts/ChainsContext";
import NotFound from "../../components/NotFound";

const txResponseTransposed = (
  prefix: string,
  tx: TxResponse,
  response: any,
  chainId: string,
  hash?: string
) => {
  if (prefix.includes("terra")) {
    return {
      distanceToNowTime: response.timestamp,
      txTime: tx.timestamp,
      txHash: tx.txhash,
      txChainID: tx.chainId,
      height: tx.height,
      gasUsed: tx.gas_used,
      gasWanted: tx.gas_wanted
    };
  }
  return {
    distanceToNowTime: response.tx_response.timestamp,
    txTime: response.tx_response.timestamp,
    txHash: hash,
    txChainID: chainId,
    height: response.tx_response.height,
    gasUsed: response.tx_response.gas_used,
    gasWanted: response.tx_response.gas_wanted
  };
};

const url = (prefix: string, txhash: string) => {
  return prefix.includes("terra")
    ? `/v1/tx/${txhash}`
    : `/cosmos/tx/v1beta1/txs/${txhash}`;
};

const TxComponent = ({ hash }: { hash: string }) => {
  const ruleSet = useLogfinderActionRuleSet();
  const logMatcher = useMemo(
    () => createLogMatcherForActions(ruleSet),
    [ruleSet]
  );
  const isClassic = useIsClassic();
  const { chainID, prefix } = useCurrentChain();
  const { data: response, progressState, isTxFound } = usePollTxHash(hash);

  if (!response) {
    return isTxFound ? (
      <Searching state={progressState} hash={hash} />
    ) : (
      <NotFound keyword={hash} />
    );
  }

  const tx = transformTx(response, chainID);
  const {
    distanceToNowTime,
    txTime,
    txHash,
    txChainID,
    height,
    gasUsed,
    gasWanted
  } = txResponseTransposed(prefix, tx, response, chainID, hash);

  const isPending = prefix.includes("terra")
    ? !response?.height
    : !response?.tx_response.height;
  const matchedMsg = getTxAllCanonicalMsgs(JSON.stringify(tx), logMatcher);

  const fee: Amount[] = get(tx, "tx.value.fee.amount");

  // status settings
  const status = isPending ? (
    <span className={c(s.status, s.pending)}>Pending</span>
  ) : !tx.code ? (
    <span className={c(s.status, s.success)}>Success</span>
  ) : (
    <span className={c(s.status, s.fail)}>Failed</span>
  );

  return (
    <>
      <h2 className={s.title}>Transaction Details</h2>
      <div className={s.header}>
        {status}
        <span className={c(s.date, s.sideLine)}>
          {formatDistanceToNowStrict(new Date(distanceToNowTime.toString()), {
            addSuffix: true
          })}
        </span>
        <span className={c(s.date, s.txTime)}>
          {format.date(txTime.toString())}
        </span>
      </div>

      {isPending && <Pending timestamp={txTime} />}
      {tx.code ? (
        <div className={s.failedMsg}>
          <Icon name="error" size={18} className={s.icon} />
          <p>{get(last(tx.logs), "log.message") || get(tx, "raw_log")}</p>
        </div>
      ) : null}

      <div className={s.list}>
        <div className={s.row}>
          <div className={s.head}>Tx Hash</div>
          <div className={s.body}>
            <div>
              {txHash}
              <Copy
                text={txHash!}
                style={{ display: "inline-block", position: "absolute" }}
              />
            </div>
          </div>
        </div>

        <div className={s.row}>
          <div className={s.head}>Network</div>
          <div className={s.body}>{txChainID}</div>
        </div>

        {isPending ? (
          <></>
        ) : (
          <div className={s.row}>
            <div className={s.head}>Block</div>
            <div className={s.body}>
              <Finder q="blocks" v={String(height)}>
                {String(height)}
              </Finder>
            </div>
          </div>
        )}

        {fee?.length ? (
          <div className={s.row}>
            <div className={s.head}>Transaction fee</div>
            <div className={s.body}>
              {fee.map((fee, key) => (
                <TxAmount index={key} {...fee} key={key} />
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}

        {isClassic ? (
          <div className={s.row}>
            <div className={s.head}>Tax</div>
            <div className={s.body}>
              <TaxRateAmount logs={tx.logs} />
            </div>
          </div>
        ) : (
          <></>
        )}

        {!isPending && (
          <div className={s.row}>
            <div className={s.head}>Gas (Used/Requested)</div>
            <div className={s.body}>
              {parseInt(String(gasUsed)).toLocaleString()}/
              {parseInt(String(gasWanted)).toLocaleString()}
            </div>
          </div>
        )}

        <div className={s.row}>
          <div className={s.head}>Memo</div>
          <div className={s.body}>
            {tx.tx.value?.memo ? tx.tx.value.memo : "-"}
          </div>
        </div>
      </div>

      {tx.tx?.value?.msg.map((msg, index) => {
        const msgInfo = matchedMsg?.[index];

        return (
          <MsgBox
            msg={msg}
            log={
              prefix.includes("terra")
                ? response.logs?.[index]
                : response.tx_response.logs?.[index].events
            }
            info={msgInfo}
            key={index}
          />
        );
      })}
    </>
  );
};

const Tx = () => {
  const { hash } = useParams();
  if (!hash) {
    throw new Error("Tx hash is not defined");
  }

  return hash ? <TxComponent hash={hash} key={hash} /> : null;
};

export default Tx;

/* hooks */
const INTERVAL = 1000;

const usePollTxHash = (txhash: string) => {
  const { chainID, prefix } = useCurrentChain();

  const [stored, setStored] = useState<any>();
  const [progress, setProgress] = useState<number>(0);
  const [testTxNumber, setTestTxNumber] = useState<number>(0);
  const [isTxFound, setIsTxFound] = useState(true);

  /* polling tx */
  const [refetchTx, setRefetchTx] = useState<boolean>(true);

  /* polling mempool tx */
  const [refetchMempool, setRefetchMempool] = useState<boolean>(false);

  const txURL = useGetQueryURL(url(prefix, txhash));

  /* query: tx */
  const txQuery = useQuery(
    [chainID, txhash, "tx"],
    () => apiClient.get<any>(txURL),
    {
      refetchInterval: INTERVAL,
      refetchOnWindowFocus: false,
      enabled: refetchTx,
      onSuccess: data => data.data && setStored(data.data)
    }
  );

  const mempoolURL = useGetQueryURL(`/v1/mempool/${txhash}`);

  /* query: mempool tx */
  const mempoolQuery = useQuery(
    [chainID, txhash, "mempool"],
    () => apiClient.get<any>(mempoolURL),
    {
      refetchInterval: INTERVAL,
      refetchOnWindowFocus: false,
      enabled: refetchMempool,
      onSuccess: data => data.data && setStored(data.data)
    }
  );

  const exceededThirtySearches = testTxNumber > 30;
  const foundTx = stored !== undefined ? true : false;

  // if tx not exists(null or no height), start polling mempool tx
  useEffect(() => {
    if (txQuery.data?.data) {
      setRefetchTx(false);
      setRefetchMempool(false);
      setIsTxFound(true);
    }

    if (!txQuery.isFetching && !txQuery.data?.data && !exceededThirtySearches) {
      setTestTxNumber((prevAttempt: number) => prevAttempt + 1);
      setRefetchMempool(true);
    }

    if (!txQuery.isFetching && !txQuery.data?.data && exceededThirtySearches) {
      setRefetchTx(false);
      setRefetchMempool(false);
      setIsTxFound(foundTx);
    }

    if (mempoolQuery.data?.data) {
      setRefetchMempool(false);
      setIsTxFound(foundTx);
    }

    setProgress(state => (state + 0.0333) % 1);
  }, [
    mempoolQuery.data,
    txQuery.data,
    txQuery.isFetching,
    txhash,
    exceededThirtySearches,
    foundTx
  ]);

  return {
    data: stored,
    progressState: progress,
    isTxFound: isTxFound
  };
};
